import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ANIMATION, prefersReducedMotion, isMobile } from './animation-config'

gsap.registerPlugin(ScrollTrigger)

interface PinSectionOptions {
  /** Distance to scroll while pinned (in vh or px) */
  scrollDistance?: string
  /** CSS selector for elements to reveal during pin */
  revealSelector?: string
  /** Start position for pin */
  start?: string
  /** Scrub intensity (true, false, or number) */
  scrub?: boolean | number
}

/**
 * Pin a section while revealing content on scroll
 * Creates the "act" experience where title stays pinned while cards reveal
 *
 * @example
 * function ActSection({ title, cards }) {
 *   const { containerRef, titleRef, contentRef } = usePinSection({
 *     scrollDistance: '200vh',
 *     revealSelector: '.card',
 *   })
 *
 *   return (
 *     <section ref={containerRef}>
 *       <div ref={titleRef} className="act-title">{title}</div>
 *       <div ref={contentRef} className="cards">
 *         {cards.map(card => <div className="card">{card}</div>)}
 *       </div>
 *     </section>
 *   )
 * }
 */
export function usePinSection<T extends HTMLElement>(options: PinSectionOptions = {}) {
  const containerRef = useRef<T>(null)
  // `titleRef` is reserved API (consumers attach it to the pinned title node); the
  // pin itself is driven by `containerRef` + `pin: true`, so the effect needn't read it.
  const titleRef = useRef<HTMLElement>(null)
  const contentRef = useRef<HTMLElement>(null)
  // Destructure to primitives so the effect deps are stable (an inline `options`
  // object would re-create the ScrollTriggers on every render).
  const { scrollDistance = '200vh', revealSelector, start = 'top top', scrub = 0.5 } = options

  useEffect(() => {
    if (!containerRef.current || !titleRef.current || !contentRef.current) return

    // On mobile / reduced-motion, skip pinning and use a simple reveal instead —
    // inside gsap.context so its ScrollTrigger is reverted on unmount (no leak).
    if (isMobile() || prefersReducedMotion()) {
      const mobileCtx = gsap.context(() => {
        const elements = revealSelector ? contentRef.current!.querySelectorAll(revealSelector) : []
        if (elements.length > 0) {
          gsap.from(elements, {
            y: ANIMATION.distance.normal,
            opacity: 0,
            duration: ANIMATION.duration.normal,
            stagger: ANIMATION.stagger.normal,
            ease: ANIMATION.ease.reveal,
            scrollTrigger: {
              trigger: containerRef.current,
              start: 'top 80%',
              toggleActions: 'play none none none',
            },
          })
        }
      }, containerRef)
      return () => mobileCtx.revert()
    }

    const ctx = gsap.context(() => {
      // Pin the section
      ScrollTrigger.create({
        trigger: containerRef.current,
        start,
        end: `+=${scrollDistance}`,
        pin: true,
        pinSpacing: true,
        anticipatePin: 1,
      })

      // Reveal elements during scroll if selector provided
      if (revealSelector && contentRef.current) {
        const elements = contentRef.current.querySelectorAll(revealSelector)

        if (elements.length > 0) {
          gsap.from(elements, {
            y: ANIMATION.distance.long,
            opacity: 0,
            filter: `blur(${ANIMATION.blur.dramatic}px)`,
            scale: ANIMATION.scale.dramatic,
            duration: ANIMATION.duration.normal,
            stagger: ANIMATION.stagger.normal,
            ease: ANIMATION.ease.reveal,
            scrollTrigger: {
              trigger: containerRef.current,
              start: 'top top',
              end: `+=${scrollDistance}`,
              scrub,
            },
          })
        }
      }
    }, containerRef)

    return () => ctx.revert()
  }, [scrollDistance, revealSelector, start, scrub])

  return { containerRef, titleRef, contentRef }
}

/**
 * Simpler pin that just pins an element without reveal animation
 * Useful for act titles that should stay visible while scrolling through cards
 */
export function useSimplePin<T extends HTMLElement>(
  scrollDistance = '100vh',
  options?: { start?: string; end?: string }
) {
  const ref = useRef<T>(null)

  useEffect(() => {
    if (!ref.current || isMobile() || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: ref.current,
        start: options?.start ?? 'top top',
        end: options?.end ?? `+=${scrollDistance}`,
        pin: true,
        pinSpacing: false,
        anticipatePin: 1,
      })
    }, ref)

    return () => ctx.revert()
  }, [scrollDistance, options?.start, options?.end])

  return ref
}
